import { Component, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/** Catches any thrown render error so a crash doesn't blank the whole app
 *  (white screen). Common trigger here: React error #185 (Maximum update
 *  depth exceeded) during drag-and-drop. The fallback offers a hard reload
 *  and dumps the message so the user can copy it back to us. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught:', error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex h-screen w-screen items-center justify-center bg-[var(--color-bg)] p-6">
          <div className="max-w-lg rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-6 shadow-[var(--shadow-modal)]">
            <h1 className="mb-3 text-lg font-semibold text-[var(--color-text-strong)]">
              Une erreur a interrompu l'application
            </h1>
            <p className="mb-3 text-sm text-[var(--color-text)]">
              Le bandeau d'erreur ci-dessous est utile à transmettre au
              support — recharger la page redémarre l'app.
            </p>
            <pre className="mb-4 max-h-48 overflow-auto rounded-xl bg-[var(--color-surface-2)] p-3 text-[11.5px] text-[var(--color-text-dim)]">
              {this.state.error.message}
              {this.state.error.stack ? '\n\n' + this.state.error.stack : ''}
            </pre>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[var(--color-accent-hover)]"
              >
                Recharger
              </button>
              <button
                type="button"
                onClick={() => this.setState({ error: null })}
                className="inline-flex items-center rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-text)] hover:bg-[var(--color-surface-3)]"
              >
                Ignorer
              </button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
